import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, Package, TrendingUp, Star, Truck, Clock, ReceiptEuro, BarChart3, ShieldCheck } from "lucide-react";
import { api, formatPrice, formatDate, imgUrl, PAYOUT_STATUS } from "../lib/api";
import { Seo } from "../components/Seo";

const MONTH_NAMES = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
const SALE_STATUS = { PAID: ["Zu versenden", "bg-amber-100 text-amber-700"], SHIPPED: ["Unterwegs", "bg-blue-100 text-blue-700"], DELIVERED: ["Zugestellt", "bg-green-100 text-green-700"], COMPLETED: ["Abgeschlossen", "bg-secondary text-foreground/70"] };
const PAYOUT_LABELS = { PENDING: "Ausstehend", COMPLETED: "Abgeschlossen", FAILED: "Fehlgeschlagen", AWAITING_KYC: "Wartet auf Verifizierung" };

function WalletHero({ data, balance }) {
  const figures = [
    { label: "Verfügbares Guthaben", value: balance?.connected ? formatPrice(balance.available) : "—", sub: balance?.connected ? `+ ${formatPrice(balance.pending)} in Bearbeitung` : "Auszahlungskonto noch nicht eingerichtet", testid: "wallet-available" },
    { label: "In Verwahrung", value: formatPrice(data.pending_amount), sub: `${data.pending_count} ${data.pending_count === 1 ? "Verkauf" : "Verkäufe"} · wird nach Schutzfrist frei`, testid: "wallet-held" },
    { label: "Gesamt ausgezahlt", value: formatPrice(data.completed_amount), sub: `${data.completed_count} Auszahlungen`, testid: "wallet-paid-out" },
  ];
  return (
    <div className="relative overflow-hidden bg-[#111111] text-white rounded-3xl" data-testid="wallet-hero">
      <div aria-hidden="true" className="absolute -top-24 -right-24 w-[380px] h-[380px] rounded-full opacity-30" style={{ background: "radial-gradient(circle, #FF6B35 0%, transparent 65%)" }} />
      <div aria-hidden="true" className="absolute -bottom-32 -left-20 w-[300px] h-[300px] rounded-full opacity-[0.12]" style={{ background: "radial-gradient(circle, #FF6B35 0%, transparent 65%)" }} />
      <div className="relative p-6 md:p-9">
        <p className="fade-up inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.25em] uppercase text-accent">
          <Wallet className="w-4 h-4" /> Geldbeutel
        </p>
        <h1 className="fade-up-1 font-display text-2xl md:text-4xl font-bold tracking-tight mt-2">Dein Guthaben im Blick.</h1>
        <div className="fade-up-2 grid sm:grid-cols-3 gap-4 md:gap-6 mt-7">
          {figures.map((f) => (
            <div key={f.label} className="rounded-2xl border border-white/10 bg-white/[0.05] p-4 md:p-5" data-testid={f.testid}>
              <p className="text-[11px] font-bold uppercase tracking-wider text-white/50">{f.label}</p>
              <p className="font-display text-2xl md:text-3xl font-bold mt-1.5">{f.value}</p>
              <p className="text-[11px] text-white/55 mt-1">{f.sub}</p>
            </div>
          ))}
        </div>
        <p className="fade-up-3 text-[11px] text-white/50 mt-5 flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-accent" /> Auszahlungen laufen automatisch auf dein hinterlegtes Bankkonto — 0 % Verkäufergebühr.
        </p>
      </div>
    </div>
  );
}

function RevenueChart({ monthly }) {
  const max = Math.max(...monthly.map((m) => m.sum), 1);
  const total = monthly.reduce((a, m) => a + m.sum, 0);
  return (
    <div className="bg-white border border-[#E5E3DA] rounded-3xl p-5 md:p-6" data-testid="revenue-chart">
      <p className="font-display font-bold text-base flex items-center gap-2.5">
        <span className="w-9 h-9 rounded-xl bg-accent/10 text-accent flex items-center justify-center"><BarChart3 className="w-4 h-4" /></span>
        Umsatz · letzte 6 Monate
      </p>
      <p className="text-xs text-muted-foreground ml-[46px] -mt-1">{formatPrice(total)} gesamt</p>
      <div className="flex items-end gap-2.5 h-36 mt-5">
        {monthly.map((m) => {
          const [y, mo] = m.month.split("-");
          const pct = Math.max(4, Math.round((m.sum / max) * 100));
          return (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-1.5 group" title={`${formatPrice(m.sum)} · ${m.count} Verkäufe`}>
              <span className="text-[10px] font-bold text-foreground/70 opacity-0 group-hover:opacity-100 transition-opacity">{formatPrice(m.sum)}</span>
              <div className="w-full rounded-t-lg bg-accent/15 relative overflow-hidden transition-colors group-hover:bg-accent/25" style={{ height: `${pct}%` }}>
                <div className="absolute bottom-0 inset-x-0 bg-accent rounded-t-lg transition-all" style={{ height: m.sum > 0 ? "100%" : "0%" }} />
              </div>
              <span className="text-[10px] font-semibold text-muted-foreground">{MONTH_NAMES[parseInt(mo, 10) - 1]}{y !== String(new Date().getFullYear()) ? ` ${y.slice(2)}` : ""}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RecentSales({ sales }) {
  return (
    <div className="bg-white border border-[#E5E3DA] rounded-3xl p-5 md:p-6" data-testid="recent-sales">
      <p className="font-display font-bold text-base flex items-center gap-2.5">
        <span className="w-9 h-9 rounded-xl bg-accent/10 text-accent flex items-center justify-center"><ReceiptEuro className="w-4 h-4" /></span>
        Letzte Verkäufe
      </p>
      {sales.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-4">Noch keine Verkäufe — sobald es losgeht, siehst du hier jede Bestellung.</p>
      ) : (
        <div className="mt-3 divide-y divide-[#F0EEE4]">
          {sales.map((o) => {
            const [label, cls] = SALE_STATUS[o.status] || [o.status, "bg-secondary"];
            return (
              <div key={o.id} className="py-3 flex items-center gap-3" data-testid={`sale-row-${o.id}`}>
                {o.item_image_ids?.[0] ? <img src={imgUrl(o.item_image_ids[0], "thumb")} alt="" className="w-11 h-11 rounded-xl object-cover border border-[#E5E3DA]" /> : <span className="w-11 h-11 rounded-xl bg-secondary flex items-center justify-center"><Package className="w-4 h-4 text-muted-foreground" /></span>}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold truncate">{o.item_title}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{o.paid_at ? formatDate(o.paid_at) : ""} · {PAYOUT_STATUS[o.payout_status] || o.payout_status}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-1 rounded-md shrink-0 ${cls}`}>{label}</span>
                <span className="font-display font-bold text-sm shrink-0">{formatPrice(o.seller_amount)}</span>
              </div>
            );
          })}
        </div>
      )}
      <Link to="/bestellungen?tab=verkaeufe" className="inline-block mt-3 text-xs font-bold text-accent hover:underline">Alle Verkäufe ansehen →</Link>
    </div>
  );
}

export default function Geldbeutel() {
  const [data, setData] = useState(null);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    api.get("/seller/dashboard").then(({ data }) => setData(data)).catch(() => setData(false));
    api.get("/seller/balance").then(({ data }) => setBalance(data)).catch(() => setBalance(null));
  }, []);

  if (data === null) return <div className="max-w-4xl mx-auto px-4 py-10"><Seo noindex title="Geldbeutel" /><div className="h-64 bg-muted rounded-3xl animate-pulse" /></div>;
  if (data === false) return <div className="max-w-4xl mx-auto px-4 py-20 text-center text-muted-foreground">Fehler beim Laden.</div>;

  const stats = [
    { label: "Umsatz gesamt", value: formatPrice(data.total_revenue), icon: TrendingUp, testid: "stat-revenue" },
    { label: "Verkauft", value: data.sold_items, icon: Package, testid: "stat-sold" },
    { label: "Zu versenden", value: data.to_ship, icon: Truck, testid: "stat-to-ship", warn: data.to_ship > 0 },
    { label: "Aktive Artikel", value: data.active_items, icon: Clock, testid: "stat-active" },
    { label: "Bewertung", value: data.rating ? `${String(data.rating.avg).replace(".", ",")} ★` : "—", sub: data.rating ? `${data.rating.count} Bewertungen` : null, icon: Star, testid: "stat-rating" },
  ];

  return (
    <div className="bg-[#FAF9F5] min-h-screen" data-testid="wallet-page">
      <Seo noindex title="Geldbeutel" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-20">
        <WalletHero data={data} balance={balance} />

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5 mt-5">
          {stats.map((c) => (
            <div key={c.label} data-testid={c.testid} className={`card-lift border rounded-2xl p-4 bg-white ${c.warn ? "border-amber-300" : "border-[#E5E3DA] hover:border-accent/40"}`}>
              <span className={`w-8 h-8 rounded-xl flex items-center justify-center ${c.warn ? "bg-amber-100 text-amber-600" : "bg-accent/10 text-accent"}`}><c.icon className="w-4 h-4" /></span>
              <p className="font-display text-lg font-bold mt-2.5">{c.value}</p>
              <p className="text-[11px] text-muted-foreground">{c.sub || c.label}</p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-5 mt-5">
          <RevenueChart monthly={data.monthly || []} />
          <RecentSales sales={data.recent_sales || []} />
        </div>

        <h2 className="font-display text-base font-bold mt-10 mb-3 flex items-center gap-3"><span aria-hidden="true" className="w-1.5 h-5 rounded-full bg-accent inline-block" /> Auszahlungshistorie</h2>
        <div className="border border-[#E5E3DA] bg-white rounded-2xl overflow-hidden" data-testid="payout-history">
          {data.payouts.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">Noch keine Auszahlungen. Nach Zustellung + Käuferschutzfrist wird automatisch ausgezahlt.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-left text-xs">
                <tr><th className="p-3">Datum</th><th className="p-3">Betrag</th><th className="p-3">Status</th><th className="p-3 hidden sm:table-cell">Referenz</th></tr>
              </thead>
              <tbody>
                {data.payouts.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="p-3">{formatDate(p.created_at)}</td>
                    <td className="p-3 font-bold">{formatPrice(p.amount)}</td>
                    <td className="p-3"><span className="text-xs bg-secondary font-semibold px-2 py-1 rounded-md">{PAYOUT_LABELS[p.status] || p.status}</span></td>
                    <td className="p-3 hidden sm:table-cell text-xs text-muted-foreground">{p.stripe_transfer_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground mt-6">Dein Verkäuferkonto und deine Artikel verwaltest du im <Link to="/verkaeufer" className="text-accent hover:underline font-semibold">Verkäuferbereich</Link>.</p>
      </div>
    </div>
  );
}
